import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { collection, getDocs, limit, query, where } from "firebase/firestore";
import { db } from "../lib/firebase.ts";
import { useSession } from "../lib/session.tsx";
import type { StringKey } from "../lib/i18n.ts";

/**
 * Global search — one box over the collections that carry the campaign's own
 * knowledge (evidence, memory) plus the two most-referenced day-to-day lists
 * (tasks, contacts). Not a full-text index: Firestore has none, so evidence
 * and memory are matched against their existing `keywords` arrays (already
 * computed server-side for AURA's own retrieval — see aura/memory.ts terms())
 * and tasks/contacts are fetched bounded and filtered client-side. Good
 * enough to find "the thing you're thinking of" without standing up a search
 * service for an app this size.
 */

function termsOf(q: string): string[] {
  return q
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .split(/[^a-z0-9]+/)
    .filter((t) => t.length > 2)
    .slice(0, 10);
}

interface Hit {
  kind: "evidence" | "memory" | "task" | "contact";
  id: string;
  title: string;
  sub?: string;
  href: string;
}

export function Search({ onClose }: { onClose: () => void }) {
  const { t, locale, profile, can } = useSession();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [hits, setHits] = useState<Hit[] | null>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function run(query_: string) {
    const org = profile?.organizationId;
    const terms = termsOf(query_);
    if (!org || terms.length === 0) {
      setHits([]);
      return;
    }
    setBusy(true);
    const results: Hit[] = [];
    const tasks: Promise<void>[] = [];

    if (can("aura.query")) {
      tasks.push(
        getDocs(
          query(
            collection(db, "evidence"),
            where("organizationId", "==", org),
            where("keywords", "array-contains-any", terms),
            limit(8),
          ),
        ).then((snap) => {
          for (const d of snap.docs) {
            results.push({
              kind: "evidence",
              id: d.id,
              title: (d.get("title") as string) ?? d.id,
              sub: d.get("reliability") as string,
              href: `/evidence/${d.id}`,
            });
          }
        }),
      );
      tasks.push(
        getDocs(
          query(
            collection(db, "memoryItems"),
            where("organizationId", "==", org),
            where("keywords", "array-contains-any", terms),
            limit(8),
          ),
        ).then((snap) => {
          for (const d of snap.docs) {
            results.push({
              kind: "memory",
              id: d.id,
              title: ((d.get("statement") as string) ?? "").slice(0, 120),
              sub: d.get("tier") as string,
              href: "/memory",
            });
          }
        }),
      );
    }

    if (can("projects.view")) {
      tasks.push(
        getDocs(
          query(collection(db, "tasks"), where("organizationId", "==", org), limit(300)),
        ).then((snap) => {
          const needle = query_.toLowerCase();
          for (const d of snap.docs) {
            const title = (d.get("title") as string) ?? "";
            if (title.toLowerCase().includes(needle)) {
              results.push({ kind: "task", id: d.id, title, href: "/work" });
            }
          }
        }),
      );
    }

    if (can("crm.view")) {
      tasks.push(
        getDocs(
          query(collection(db, "contacts"), where("organizationId", "==", org), limit(500)),
        ).then((snap) => {
          const needle = query_.toLowerCase();
          for (const d of snap.docs) {
            const name = (d.get("name") as string) ?? "";
            if (name.toLowerCase().includes(needle)) {
              results.push({
                kind: "contact",
                id: d.id,
                title: name,
                sub: d.get("type") as string,
                href: "/contacts",
              });
            }
          }
        }),
      );
    }

    await Promise.all(tasks);
    setHits(results.slice(0, 30));
    setBusy(false);
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    void run(q);
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        paddingTop: "12vh",
        zIndex: 100,
      }}
      onClick={onClose}
    >
      <div
        className="card"
        style={{
          width: "min(560px, 92vw)",
          maxHeight: "70vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <form onSubmit={submit}>
          <input
            autoFocus
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={
              locale === "es"
                ? "Buscar evidencia, memoria, tareas, contactos…"
                : "Search evidence, memory, tasks, contacts…"
            }
            style={{ width: "100%", fontSize: "1rem" }}
          />
        </form>
        <div style={{ overflowY: "auto", marginTop: 10 }}>
          {busy && <div className="faint small">{t("common.loading")}</div>}
          {!busy && hits !== null && hits.length === 0 && (
            <div className="faint small">{t("search.empty")}</div>
          )}
          {!busy &&
            hits?.map((h) => (
              <button
                key={`${h.kind}-${h.id}`}
                type="button"
                className="subtle"
                style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 6px" }}
                onClick={() => {
                  navigate(h.href);
                  onClose();
                }}
              >
                <span className="tag tag-neutral" style={{ marginInlineEnd: 6 }}>
                  {t(`search.kind.${h.kind}` as StringKey)}
                </span>
                {h.title}
                {h.sub && <span className="faint small"> · {h.sub}</span>}
              </button>
            ))}
        </div>
      </div>
    </div>
  );
}
